import type {
  CreateWebhookSubscriptionRequest,
  UpdateWebhookSubscriptionRequest,
  WebhookDeliveryStatus,
  WebhookDeliverySummary,
  WebhookReplayMode,
  WebhookSubscriptionHealth,
  WebhookSubscriptionSummary,
  WebhookTestResult,
} from '@ofmos/shared/webhook';
import type { WebhookClient } from '../client';
import type { Transport } from './transport';

/**
 * `WebhookClient` over `/api/webhooks` (webhooks.md section 7).
 *
 * Thin by design, like the pending client: every rule that matters - who may create a
 * subscription, whether a replay can rebuild from the event, when a secret may be shown -
 * is enforced on the server, and a second opinion here would only ever be a stale one.
 * The screens surface the server's refusal instead of predicting it.
 */
export class HttpWebhookClient implements WebhookClient {
  constructor(private readonly transport: Transport) {}

  async list(): Promise<{
    readonly subscriptions: readonly WebhookSubscriptionSummary[];
    readonly health: Readonly<Record<string, WebhookSubscriptionHealth>>;
  }> {
    return this.transport.request({ method: 'GET', path: '/api/webhooks' });
  }

  async get(id: string, reveal = false): Promise<WebhookSubscriptionSummary> {
    const response = await this.transport.request<{
      subscription: WebhookSubscriptionSummary;
    }>({
      method: 'GET',
      path: `/api/webhooks/${id}`,
      // Absent rather than `false`: the reveal is session-gated on the server, so asking
      // for it on every read would 403 an admin who is holding an API key.
      query: reveal ? { reveal: 'true' } : {},
    });
    return response.subscription;
  }

  async create(input: CreateWebhookSubscriptionRequest): Promise<WebhookSubscriptionSummary> {
    const response = await this.transport.request<{
      subscription: WebhookSubscriptionSummary;
    }>({ method: 'POST', path: '/api/webhooks', body: input });
    // Carries `secret`, and this is the only response that ever will without an explicit
    // reveal. The dialog shows it once and says so.
    return response.subscription;
  }

  async update(
    id: string,
    input: UpdateWebhookSubscriptionRequest,
  ): Promise<WebhookSubscriptionSummary> {
    const response = await this.transport.request<{
      subscription: WebhookSubscriptionSummary;
    }>({ method: 'PATCH', path: `/api/webhooks/${id}`, body: input });
    return response.subscription;
  }

  async remove(id: string): Promise<void> {
    await this.transport.requestMaybe({ method: 'DELETE', path: `/api/webhooks/${id}` });
  }

  async rotateSecret(id: string): Promise<WebhookSubscriptionSummary> {
    const response = await this.transport.request<{
      subscription: WebhookSubscriptionSummary;
    }>({ method: 'POST', path: `/api/webhooks/${id}/rotate-secret`, body: {} });
    return response.subscription;
  }

  async test(id: string): Promise<WebhookTestResult> {
    const response = await this.transport.request<{ result: WebhookTestResult }>({
      method: 'POST',
      path: `/api/webhooks/${id}/test`,
      body: {},
    });
    return response.result;
  }

  async deliveries(
    id: string,
    filter: { readonly status?: WebhookDeliveryStatus; readonly limit?: number } = {},
  ): Promise<readonly WebhookDeliverySummary[]> {
    const response = await this.transport.request<{
      deliveries: readonly WebhookDeliverySummary[];
    }>({
      method: 'GET',
      path: `/api/webhooks/${id}/deliveries`,
      query: {
        ...(filter.status === undefined ? {} : { status: filter.status }),
        limit: filter.limit ?? 50,
      },
    });
    return response.deliveries;
  }

  async replay(deliveryId: string, mode: WebhookReplayMode): Promise<WebhookDeliverySummary> {
    const response = await this.transport.request<{ delivery: WebhookDeliverySummary }>({
      method: 'POST',
      path: `/api/webhook-deliveries/${deliveryId}/replay`,
      body: { mode },
    });
    return response.delivery;
  }
}
